// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingStep, setOnboardingStep] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        loadProfile(session.user);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        loadProfile(session.user);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (authUser) => {
    try {
      // Try by auth_user_id first
      let { data, error } = await supabase
        .from('users')
        .select('*, company:company_profile(*)')
        .eq('auth_user_id', authUser.id)
        .single();
      if (error || !data) {
        // Fallback: try by email
        const res = await supabase
          .from('users')
          .select('*, company:company_profile(*)')
          .eq('email', authUser.email)
          .single();
        data = res.data;
      }
      let profile = data || { email: authUser.email, full_name: authUser.email.split('@')[0] };
      // IOD is single-tenant — if user has no company_id, look up the IOD company
      if (!profile.company_id) {
        try {
          const { data: companyRows } = await supabase
            .from('company_profile')
            .select('id')
            .limit(1);
          if (companyRows && companyRows.length > 0) {
            profile = { ...profile, company_id: companyRows[0].id };
            console.log('[IOD Auth] Assigned company_id from company_profile:', companyRows[0].id);
          } else {
            console.warn('[IOD Auth] No company_profile rows found — customers/jobs will not load');
          }
        } catch (e) {
          console.error('[IOD Auth] company_profile lookup failed:', e);
        }
      } else {
        console.log('[IOD Auth] User has company_id:', profile.company_id);
      }
      setUser(profile);
      // Determine onboarding step
      // Check BOTH localStorage (this domain) AND Supabase metadata / other domain keys
      // This prevents re-asking password change if user already did it on another domain (e.g. islandoverheaddoors.pages.dev)
      const pwChanged = localStorage.getItem('iod_password_changed') || profile.password_changed_at || authUser?.user_metadata?.password_changed;
      const ndaSigned = authUser?.user_metadata?.nda_signed_at || profile.nda_signed_at || localStorage.getItem('iod_nda_signed');
      const tutorialDone = localStorage.getItem('iod_tutorial_done');
      // Email setup is OPTIONAL — accessible from Settings, never blocks login
      if (!pwChanged) setOnboardingStep('password');
      else if (!ndaSigned) setOnboardingStep('nda');
      else if (!tutorialDone) setOnboardingStep('tutorial');
      else setOnboardingStep(null);
    } catch (err) {
      // Even on error, try to get company_id for single-tenant IOD
      let fallbackProfile = { email: authUser.email, full_name: authUser.email.split('@')[0] };
      try {
        const { data: companyRows } = await supabase
          .from('company_profile')
          .select('id')
          .limit(1);
        if (companyRows && companyRows.length > 0) {
          fallbackProfile = { ...fallbackProfile, company_id: companyRows[0].id };
          console.log('[IOD Auth] Fallback: assigned company_id:', companyRows[0].id);
        } else {
          console.warn('[IOD Auth] Fallback: no company_profile rows found');
        }
      } catch (e) {
        console.error('[IOD Auth] Fallback company_profile lookup failed:', e);
      }
      setUser(fallbackProfile);
      const pwChanged = localStorage.getItem('iod_password_changed') || authUser?.user_metadata?.password_changed;
      const ndaSigned = authUser?.user_metadata?.nda_signed_at || localStorage.getItem('iod_nda_signed');
      const tutorialDone = localStorage.getItem('iod_tutorial_done');
      if (!pwChanged) setOnboardingStep('password');
      else if (!ndaSigned) setOnboardingStep('nda');
      else if (!tutorialDone) setOnboardingStep('tutorial');
      else setOnboardingStep(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    if (error) throw error;
    return data;
  };

  const signUp = async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } }
    });
    if (error) throw error;
    try {
      await supabase.from('app_events').insert({
        event_type: 'signup',
        app_name: 'iod-app',
        user_email: email,
        metadata: { full_name: fullName, source: 'web' },
      });
    } catch (e) { /* silent */ }
    return data;
  };

  const completeOnboardingStep = async (step) => {
    if (step === 'password') {
      localStorage.setItem('iod_password_changed', 'true');
      // Also persist to Supabase so other domains see it
      try { await supabase.auth.updateUser({ data: { password_changed: true } }); } catch(e) { /* silent */ }
      setOnboardingStep('nda');
    } else if (step === 'nda') {
      localStorage.setItem('iod_nda_signed', 'true');
      try { await supabase.auth.updateUser({ data: { nda_signed_at: new Date().toISOString() } }); } catch(e) { /* silent */ }
      setOnboardingStep('tutorial');
    } else if (step === 'tutorial') {
      localStorage.setItem('iod_tutorial_done', 'true');
      // Check if user needs email setup (dispatchers, managers, owners)
      const emailSetupRoles = ['dispatcher', 'manager', 'owner', 'accounting'];
      const emailSetupDone = localStorage.getItem('iod_email_setup_done');
      if (emailSetupRoles.includes(user?.role) && !emailSetupDone) {
        setOnboardingStep('email_setup');
      } else {
        setOnboardingStep(null);
      }
    } else if (step === 'email_setup') {
      localStorage.setItem('iod_email_setup_done', 'true');
      setOnboardingStep(null);
    }
  };
  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setOnboardingStep(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut , onboardingStep, completeOnboardingStep }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
